import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import { RegisterBanksSelect } from '@/modules/common/components/RegisterBanksSelect.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import { IssuerTransactionsOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import {
  IssuerAddCashMovementModal,
  mapCashMovementTypeToText,
} from '@/modules/issuer-dashboard/components/portfolios/IssuerAddCashMovementModal.tsx';
import { IssuerAddUpdatePortfolioTransactionModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdatePortfolioTransactionModal.tsx';
import { IssuerCashMovementFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerCashMovementFullInfoModal.tsx';
import { mapPortfolioTransactionType } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import { mapStatusToPlusMinusType } from '@/modules/issuer-dashboard/components/portfolios/mapStatusToPlusMinusType.ts';
import {
  useIssuerDeletePortfolioCashMovementMutation,
  useIssuerPortfolioCashMovementInfiniteListQuery,
  useIssuerPortfolioTransactionByIdQuery,
  useIssuerRestorePortfolioCashMovementMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  FilterItem,
  ModalProviderContext,
  SelectOption,
  SelectPaginateOption,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  getSelectOptionByEnum,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DotsHorizontalIcon } from 'ui/icons';
import { fixNameEnum, goodFormatNumberCustom } from 'ui/utils';
import {
  GetPortfolioCashMovementListV1SelfCustodyCashMovementGetParams,
  PaymentStatus,
  PortfolioCashMovementPaginateResponse,
  RegisterBankExtended,
} from 'api';

type TypeHead = 'ID' | 'Status' | 'Type' | 'Sender' | 'Destination' | 'Total' | 'Date' | '';

const headItems: TableHeadColumn<TypeHead>[] = [
  { title: 'ID' },
  { title: 'Status' },
  { title: 'Type' },
  { title: 'Sender' },
  {
    title: 'Destination',
  },
  {
    title: 'Total',
  },
  {
    title: 'Date',
    className: 'min-w-[140px]',
  },
  { title: '', className: 'w-10 max-w-10' },
];

export type CashMovementsTabFilters = Omit<
  GetPortfolioCashMovementListV1SelfCustodyCashMovementGetParams,
  'page_size' | 'page' | 'sorted_by'
>;

export const CashMovementsTab = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { openModal } = useContext(ModalProviderContext);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { isMobile } = useIsMobile();
  const [filters, setFilters] = useState<CashMovementsTabFilters>({});
  const [archived, setArchived] = useState(false);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const {
    data: issuerPaymentsList,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetching,
  } = useIssuerPortfolioCashMovementInfiniteListQuery(
    {
      page: 1,
      page_size: 30,
      ...filters,
      archived,
      register_id: Number(registerId),
    },
    false,
  );

  const handleOpenOperationInfoModal = useCallback(
    (item: PortfolioCashMovementPaginateResponse) => {
      if (item.source_type === 'register') {
        if (item.transaction_id) {
          openModal(IssuerTransactionsOperationInfoModal, {
            id: item.transaction_id,
            registerId,
            registerCountry,
            isDeleted: archived,
          });
          return;
        }
        if (item.payment_id) {
          openModal(IssuerActivitiesOperationInfoModal, {
            paymentId: item.payment_id,
            registerId,
            registerCountry,
            isDeleted: archived,
          });
          return;
        }
        if (item.dividend_event_id) {
          openModal(DividendOperationInfoModal, {
            id: item.dividend_event_id,
            registerId,
            registerCountry,
            isDeleted: archived,
          });
          return;
        }
      }
      openModal(IssuerCashMovementFullInfoModal, {
        item,
        status: item.status || item?.transaction?.status || item?.dividend_event?.status || '',
        registerId,
        registerCountry,
      });
    },
    [archived, openModal, registerCountry, registerId],
  );

  const handleGoToHolderPage = useCallback(
    (entityId?: number) => {
      if (!entityId) return;

      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(entityId.toString()),
          search: params.toString(),
        },
        { replace: false, state: { from: pathname + '?' + params.toString() } },
      );
    },
    [navigate, params, pathname],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!issuerPaymentsList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    issuerPaymentsList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          const currency =
            data?.to_bank?.bank_currency?.toUpperCase() ||
            data?.from_bank?.bank_currency?.toUpperCase();
          const canShowActions = archived ? data.source_type === 'manual' : data.can_edit_source;

          const body: BodyCell[] = [
            {
              cell: (
                <div className="flex items-center gap-3">
                  <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">
                    #{data.id}
                  </span>
                </div>
              ),
            },
            {
              cell: (
                <ActivityStatusBadge
                  status={
                    data.status || data?.transaction?.status || data?.dividend_event?.status || ''
                  }
                />
              ),
            },
            {
              cell: (
                <span className="flex gap-2 items-center">
                  <span>{mapCashMovementTypeToText[data.direction]}</span>
                  <CreatedByAiLabel aiStatus={data.ai_status} />
                  {(data?.transaction?.type ||
                    data?.dividend_event?.type ||
                    data?.portfolio_transaction?.transaction_type) && (
                    <span className="text-gray-500 text-xs">
                      (
                      {data?.portfolio_transaction?.transaction_type
                        ? mapPortfolioTransactionType[data?.portfolio_transaction?.transaction_type]
                        : fixNameEnum(
                            data?.transaction?.type ||
                              getRegisterTypeStrings('Dividend', { capitalize: true }),
                          )}
                      )
                    </span>
                  )}
                </span>
              ),
            },
            {
              cell: (
                <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                  {data?.from_bank ? (
                    <>
                      <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                        <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                          {data?.from_bank?.bank_account_name}
                        </span>
                        {/* <span className="text-gray-500"> */}
                        {/*   ({data?.from_bank?.bank_name.toUpperCase()}) */}
                        {/* </span> */}
                      </span>
                      <span className="text-gray-400">
                        {data?.from_bank?.bank_currency.toUpperCase()}
                      </span>
                    </>
                  ) : data?.shareholder_entity?.entity_name ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_entity?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_entity?.id,
                          },
                        )}
                      >
                        {data.shareholder_entity?.entity_name ?? '-'}
                      </div>
                    </>
                  ) : data?.shareholder_user ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_user?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_user?.id,
                          },
                        )}
                      >
                        {data?.shareholder_user?.first_name} {data?.shareholder_user?.last_name}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </span>
              ),
            },
            {
              cell: (
                <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                  {data?.to_bank ? (
                    <>
                      <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                        <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                          {data?.to_bank?.bank_account_name}
                        </span>
                        {/* <span className="text-gray-500"> */}
                        {/*   ({data?.to_bank?.bank_name.toUpperCase()}) */}
                        {/* </span> */}
                      </span>
                      <span className="text-gray-400">
                        {data?.to_bank?.bank_currency.toUpperCase()}
                      </span>
                    </>
                  ) : data?.shareholder_entity?.entity_name ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_entity?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_entity?.id,
                          },
                        )}
                      >
                        {data.shareholder_entity?.entity_name ?? '-'}
                      </div>
                    </>
                  ) : data?.shareholder_user ? (
                    <>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          handleGoToHolderPage(data.shareholder_user?.id);
                        }}
                        className={classNames(
                          'text-sm whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis',
                          {
                            'text-blue-700 font-medium cursor-pointer hover:underline':
                              !!data.shareholder_user?.id,
                          },
                        )}
                      >
                        {data?.shareholder_user?.first_name} {data?.shareholder_user?.last_name}
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </span>
              ),
            },
            {
              cell: (
                <span className="whitespace-nowrap text-nowrap">
                  {mapStatusToPlusMinusType[data.direction]}
                  {goodFormatNumberCustom(data.amount, currency)}
                </span>
              ),
            },
            {
              cell: <div>{dayjs(data.effective_date).format('DD/MM/YYYY HH:mm')}</div>,
            },
            {
              cell: canShowActions ? (
                <CashMovementMoreButton item={data} archived={archived} />
              ) : null,
            },
          ];

          return {
            cells: body,
            onClick: () => handleOpenOperationInfoModal(data),
          };
        }),
      );
    });

    return result;
  }, [
    archived,
    getRegisterTypeStrings,
    handleGoToHolderPage,
    handleOpenOperationInfoModal,
    issuerPaymentsList,
  ]);

  const dateValue =
    filters.effective_date_from && filters.effective_date_to
      ? ([dayjs(filters.effective_date_from), dayjs(filters.effective_date_to)] as [Dayjs, Dayjs])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              effective_date_from: date[0].toISOString(),
              effective_date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['effective_date_from', 'effective_date_to']);
      });
    },
    [setFilters],
  );

  const handleChangeStatus = useCallback(
    (status: PaymentStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeBank = useCallback(
    (option: SelectPaginateOption<RegisterBankExtended> | null) => {
      setFilters(filters =>
        option ? { ...filters, bank_id: option?.value?.id } : omit(filters, 'bank_id'),
      );
    },
    [setFilters],
  );

  const handleAddPayment = useCallback(() => {
    openModal(IssuerAddCashMovementModal);
  }, [openModal]);

  const optionsStatus = useMemo<SelectOption<PaymentStatus>[]>(
    () =>
      getSelectOptionByEnum<PaymentStatus>(PaymentStatus).map(item => ({
        ...item,
        label: item.label?.replace('Unsuccessfull', 'Unsuccessful'),
      })),
    [],
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full">
          <FilterItem
            placeholder="Status"
            type="select"
            value={filters.status}
            options={optionsStatus}
            onChange={handleChangeStatus}
            className="rounded-lg"
            classes={{ element: 'w-40' }}
          />
          <FilterItem type="custom" className="rounded-lg" classes={{ element: 'py-0' }}>
            <RegisterBanksSelect
              onChange={handleChangeBank}
              value={filters.bank_id}
              placeholder="Bank"
              clearable={true}
              showBorderBottom={false}
              className="w-[370px]"
              prefillBank={false}
              hideType={true}
            />
          </FilterItem>
          <FilterItem
            placeholder="Date"
            type="date"
            onChange={handleChangeDate}
            value={dateValue}
            className="rounded-lg"
          />
          {isFetching && <Spinner size="md" variant="primary" />}
        </div>
        {/* <TransactionsHeaderAction */}
        {/*   buttonName="Add new payment" */}
        {/*   openTypeTransaction="Payment" */}
        {/*   className="w-[170px]" */}
        {/*   fromPortfolio={true} */}
        {/* /> */}
        <div className="flex gap-4 items-center">
          <ArchiveSwitch active={archived} onChange={setArchived} />
          <Button className="w-[170px]" onClick={handleAddPayment} disabled={archived}>
            Add cash movement
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

const CashMovementMoreButton = ({
  item,
  archived,
}: {
  item: PortfolioCashMovementPaginateResponse;
  archived: boolean;
}) => {
  return item.source_type === 'portfolio' && item.source_kind === 'portfolio_transaction' ? (
    <PortfolioSourceMoreButton item={item} />
  ) : (
    <ManualCashMovementMoreButton item={item} archived={archived} />
  );
};

const PortfolioSourceMoreButton = ({ item }: { item: PortfolioCashMovementPaginateResponse }) => {
  const { openModal } = useContext(ModalProviderContext);
  const { refetch: fetchPortfolioTransaction } = useIssuerPortfolioTransactionByIdQuery(
    { register_id: item.register_id, transactionId: item.source_id },
    true,
  );
  const items = useMemo<DropdownMenuItem[]>(
    () => [
      {
        label: 'Edit transaction',
        onClick: async () => {
          const { data } = await fetchPortfolioTransaction();
          if (data?.transaction) {
            openModal(IssuerAddUpdatePortfolioTransactionModal, {
              item: data.transaction,
              portfolioId: data.transaction.portfolio_id,
            });
          }
        },
      },
    ],
    [fetchPortfolioTransaction, openModal],
  );

  return <CashMovementActionsDropdown items={items} />;
};

const ManualCashMovementMoreButton = ({
  item,
  archived,
}: {
  item: PortfolioCashMovementPaginateResponse;
  archived: boolean;
}) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: archiveMovement } = useIssuerDeletePortfolioCashMovementMutation();
  const { mutateAsync: restoreMovement } = useIssuerRestorePortfolioCashMovementMutation();
  const items = useMemo<DropdownMenuItem[]>(
    () =>
      archived
        ? [
            {
              label: 'Restore',
              onClick: () =>
                openModal(ConfirmModal, {
                  title: <span>Restore cash movement #{item.id}</span>,
                  onConfirm: async () => {
                    await restoreMovement(item.id);
                  },
                }),
            },
          ]
        : [
            {
              label: 'Edit',
              onClick: () => openModal(IssuerAddCashMovementModal, { item }),
            },
            {
              label: 'Archive',
              onClick: () =>
                openModal(ConfirmModal, {
                  title: <span>Archive cash movement #{item.id}</span>,
                  onConfirm: async () => {
                    await archiveMovement(item.id);
                  },
                }),
            },
          ],
    [archiveMovement, archived, item, openModal, restoreMovement],
  );

  return <CashMovementActionsDropdown items={items} />;
};

const CashMovementActionsDropdown = ({ items }: { items: DropdownMenuItem[] }) => {
  return (
    <div onClick={event => event.stopPropagation()}>
      <DropdownMenu
        items={items}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        autoPlace
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};
